import { useMemo, useState, useEffect } from "react";
import { buildQueryString } from "../utils/buildQueryString";
import axios from "axios";
import AxiosConfig from "src/utilities/AxiosConfig";

export interface PaginatedResult<RowT> {
	rows: RowT[];
	total: number;
	loading: boolean;
	error?: unknown;
}

/**
 * A custom hook to handle paginated data, supporting both local and server-side modes.
 *
 * @template RowT - The type of the data rows.
 *
 * @param opts - Options for configuring the hook.
 * @param opts.data - An optional array of data for local mode. If provided, the hook will filter and paginate this data locally.
 * @param opts.endpoint - An optional API endpoint for server mode. If provided, the hook will fetch paginated data from the server.
 * @param opts.page - The current page number (1-based index).
 * @param opts.pageSize - The number of rows per page.
 * @param opts.filterQuery - An optional object containing key-value pairs for filtering the data.
 *
 * @returns A `PaginatedResult` object containing the following properties:
 * - `rows`: The current page of data rows.
 * - `total`: The total number of rows available (after filtering).
 * - `loading`: A boolean indicating whether the data is currently being loaded.
 * - `error`: An optional error object if the server request fails.
 *
 * @throws {Error} If both `data` and `endpoint` are provided, or if neither is provided.
 *
 * @remarks
 * - In local mode, the hook filters and paginates the provided `data` array based on the `filterQuery`, `page`, and `pageSize`.
 * - In server mode, the hook fetches data from the specified `endpoint` using the provided query parameters.
 * - The hook uses `useMemo` to optimize filtering and pagination in local mode, and `useEffect` to handle server requests in server mode.
 * - The `loading` state is `true` only when fetching data from the server.
 * - Use const { filters, query } = useFiltersFromQuery(location.search, typeMap); to manage the query string in the URL.
 *
 * @example
 * // Local mode usage
 * const { rows, total, loading } = usePaginatedData({
 *   data: myDataArray,
 *   page: 1,
 *   pageSize: 10,
 *   filterQuery: { status: 'active' },
 * });
 *
 * @example
 * // Server mode usage
 * const { rows, total, loading, error } = usePaginatedData({
 *   endpoint: '/api/items',
 *   page: 2,
 *   pageSize: 20,
 *   filterQuery: { category: 'books' },
 * });
 */
export function usePaginatedData<RowT>(opts: {
	data?: RowT[]; // Local mode
	endpoint?: string; // Server mode
	page: number;
	pageSize: number;
	filterQuery?: Record<string, string | number | boolean>;
}): PaginatedResult<RowT> {
	const { data, endpoint, page, pageSize, filterQuery } = opts;

	if (!data && !endpoint) {
		throw new Error("Must provide either data or endpoint.");
	}

	// Memoized query string object
	const queryString = useMemo(
		() => ({ page, page_size: pageSize, ...filterQuery }),
		[page, pageSize, filterQuery],
	);

	// Local mode: filter and paginate the data, server mode filtered by the server
	const localResult = useMemo(() => {
		if (endpoint || !data) return null; // Skip if in server mode

		// Apply column-value filters from `extra`
		const filteredData = data.filter((row) => {
			if (!filterQuery) return true; // No filters, include all rows
			return Object.entries(filterQuery).every(([key, value]) => {
				if (value === undefined || value === "") return true; // Ignore empty filters
				return (row as Record<string, unknown>)[key] === value; // Match key-value pair
			});
		});

		// Paginate the filtered data
		const total = filteredData.length;
		const startIndex = (page - 1) * pageSize;
		const rows = filteredData.slice(startIndex, startIndex + pageSize);

		return { rows, total };
	}, [endpoint, data, page, pageSize, filterQuery]);

	const [remoteResult, setRemoteResult] = useState<PaginatedResult<RowT>>({
		rows: [],
		total: 0,
		loading: !!endpoint, // Loading only if `endpoint` is provided
	});

	useEffect(() => {
		if (!endpoint) return; // Skip if in local mode

		const cancelTokenSource = axios.CancelToken.source();
		const queryStringStr = buildQueryString(queryString);

		// Construct the full URL
		const url = endpoint.startsWith("/")
			? `${process.env.REACT_APP_API_URL}${endpoint}?${queryStringStr}`
			: `${endpoint}?${queryStringStr}`;

		// Set loading state
		setRemoteResult((prev) => ({ ...prev, loading: true, error: undefined }));

		// Fetch data from the server
		axios
			.get<{
				items: RowT[];
				pagination?: { total_count?: number };
				total?: number;
				data?: RowT[];
			}>(url, {
				...AxiosConfig.configValue("min"),
				cancelToken: cancelTokenSource.token,
			})
			.then(({ data }) => {
				const rows = data.items ?? data.data ?? [];
				const total = data.pagination?.total_count ?? data.total ?? rows.length;

				setRemoteResult({ rows, total, loading: false });
			})
			.catch((error) => {
				if (axios.isCancel(error)) return; // Ignore if request was canceled
				console.error(error);
				setRemoteResult({ rows: [], total: 0, loading: false, error });
			});

		// Cleanup on component unmount
		return () => cancelTokenSource.cancel();
	}, [endpoint, queryString]);

	return localResult ? { ...localResult, loading: false } : remoteResult;
}
